import { AxiosResponse } from 'axios';

import api from './api';

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

const MAX_PAGES = 50;

// DRF's PageNumberPagination serves a fixed page size with no
// page_size_query_param: fetch page 1, derive the page count from
// `count`, and fetch the remaining pages in parallel.
const fetchAllPages = async <T>(
  path: string,
  query = ''
): Promise<AxiosResponse<PaginatedResponse<T>>> => {
  const prefix = query ? `${query}&` : '';
  const fetchPage = (page: number): Promise<AxiosResponse<PaginatedResponse<T>>> =>
    api.get(`${path}?${prefix}page=${page}`);

  const first = await fetchPage(1);
  const { count, results } = first.data;
  if (!first.data.next || results.length === 0) return first;

  const pageCount = Math.min(Math.ceil(count / results.length), MAX_PAGES);
  const rest = await Promise.all(Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(i + 2)));
  const allResults = [...results, ...rest.flatMap((res) => res.data.results)];
  const next = count > pageCount * results.length ? first.data.next : null;
  return { ...first, data: { ...first.data, next, results: allResults } };
};

export default fetchAllPages;
